/**
 * ChordBackdrop.tsx (GRE-122 Phase B)
 *
 * Renders colored rectangles behind melody notes to visualize chord regions
 * and their quality (major, minor, diminished, augmented).
 *
 * Each chord gets a colored rectangle spanning its duration, with a label
 * at the top showing the chord name.
 */

import React, { useMemo } from 'react';
import type { Chord } from '@/types';
import { getScaleDegreeColor } from '@/styles/colors';
import styles from './ChordBackdrop.module.css';

interface ChordBackdropProps {
  chords: Chord[];
  width: number;
  height: number;
  beatWidth: number;
  zoom: number;
}

/**
 * Get background color based on chord quality
 */
function getBackdropColor(chord: Chord): string {
  // Use scale degree color as base
  return getScaleDegreeColor(chord.scaleDegree, chord.mode);
}

/**
 * Get quality-specific CSS class modifier
 */
function getQualityClass(chord: Chord): string {
  // Check basic triadic quality
  if (chord.quality === 'minor' || chord.quality.includes('min')) {
    return styles.minor;
  }
  if (chord.quality === 'diminished' || chord.quality === 'dim7' || chord.quality === 'halfdim7') {
    return styles.diminished;
  }
  if (chord.quality === 'augmented') {
    return styles.augmented;
  }
  // Default for major and seventh chords
  return styles.backdrop;
}

/**
 * Get chord label text (root + quality shorthand)
 */
function getChordLabel(chord: Chord): string {
  const degreeLabels: Record<number, string> = {
    1: 'I',
    2: 'ii',
    3: 'iii',
    4: 'IV',
    5: 'V',
    6: 'vi',
    7: 'vii°',
  };

  const degreeLabel = degreeLabels[chord.scaleDegree] || '?';

  // Add quality indicator
  if (chord.quality === 'minor' || chord.quality.includes('min')) {
    return degreeLabel;
  }
  if (chord.quality === 'diminished' || chord.quality === 'dim7' || chord.quality === 'halfdim7') {
    return chord.scaleDegree === 7 ? degreeLabel : `${degreeLabel}°`;
  }
  if (chord.quality === 'augmented') {
    return `${degreeLabel}+`;
  }

  return degreeLabel;
}

const ChordBackdropComponent: React.FC<ChordBackdropProps> = ({
  chords,
  height,
  beatWidth,
  zoom,
}) => {
  const backdropRects = useMemo(() => {
    return chords.map((chord) => {
      const x = chord.startBeat * beatWidth * zoom;
      const rectWidth = chord.duration * beatWidth * zoom;
      const color = getBackdropColor(chord);
      const label = getChordLabel(chord);
      const qualityClass = getQualityClass(chord);

      return {
        id: chord.id,
        x,
        y: 0,
        width: rectWidth,
        height,
        color,
        label,
        qualityClass,
      };
    });
  }, [chords, beatWidth, zoom]);

  return (
    <g className={styles.backdropGroup}>
      {backdropRects.map((rect) => (
        <g key={rect.id}>
          {/* Background rectangle */}
          <rect
            x={rect.x}
            y={rect.y}
            width={Math.max(rect.width, 1)} // Ensure minimum width of 1px
            height={rect.height}
            fill={rect.color}
            className={rect.qualityClass}
            pointerEvents="none"
          />

          {/* Chord label at top */}
          {rect.width > 20 && (
            <text
              x={rect.x + 4}
              y={14}
              className={styles.chordLabel}
              pointerEvents="none"
            >
              {rect.label}
            </text>
          )}
        </g>
      ))}
    </g>
  );
};

ChordBackdropComponent.displayName = 'ChordBackdrop';

export const ChordBackdrop = React.memo(ChordBackdropComponent);

export default ChordBackdrop;
